/**
 * One loader for every screen.
 *
 * The Phase-2 super-app screens were each hand-rolling the same four lines: a `useState` for the data,
 * a `useState` for the error, a `.then`, a `.catch`. That is where the states quietly drift: one screen
 * forgets the skeleton and flashes an empty layout before data arrives, another retries by reloading
 * the whole page, which on a slow connection re-downloads the bundle and drops everything.
 *
 * This gives every screen the same three states and a `reload` that REFETCHES rather than reloading the
 * document, so retry costs one request, not a cold start.
 */

import { useCallback, useEffect, useState } from 'react';

export interface Resource<T> {
  data: T | null;
  error: boolean;
  loading: boolean;
  reload: () => void;
}

export function useResource<T>(fetcher: () => Promise<T>, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  // The fetcher is a fresh closure each render, so it cannot be the dependency: including it would
  // refetch forever. The caller's `deps` are the dependency, which is what makes a screen refetch when
  // its input changes and only then.
  const run = useCallback(
    () => {
      let live = true;
      setError(false);
      setLoading(true);
      fetcher()
        .then((d) => {
          if (!live) return;
          setData(d);
          setLoading(false);
        })
        .catch(() => {
          if (!live) return;
          setError(true);
          setLoading(false);
        });
      return () => {
        live = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  useEffect(() => run(), [run]);

  return { data, error, loading, reload: run };
}
